import axios from "axios";

import { Analytics, AnalyticsEvents } from "modules/analytics";

import { get as apiGet, post as apiPost } from "../../api/index";

import { AdonisRecommendationModel } from "./adonis.types";
import { AdonisRecommendation } from "./IAdonisRecommendation";
import { AnthropicStreamHandler } from "./streaming/anthropic-stream-handler";

const warmupCache = new Map<string, Promise<boolean>>();

export const warmAdonisCache = async (sessionId: string): Promise<boolean> => {
  if (warmupCache.has(sessionId)) {
    const existingPromise = warmupCache.get(sessionId);
    if (existingPromise) {
      return existingPromise;
    }
  }

  const warmupPromise = (async () => {
    try {
      const response = await apiPost("/adonis/warm", {
        sessionId,
      });

      if (!response) {
        throw new Error(`Warmup failed`);
      }

      return response ?? false;
    } catch (error) {
      console.error("Error warming cache:", error);
      return false;
    } finally {
      warmupCache.delete(sessionId);
    }
  })();

  warmupCache.set(sessionId, warmupPromise);

  return warmupPromise;
};

export const streamWithHandler = async (
  sessionId: string,
  recommendation: string,
  handler: AnthropicStreamHandler
): Promise<void> => {
  try {
    const response = await createStreamingResponse(sessionId, recommendation);
    if (!response.body) {
      throw new Error("ReadableStream not supported");
    }
    await handler.processStream(response.body);
  } catch (error) {
    console.error("Error creating recommendation with SSE:", error);
    throw error;
  }
};

// New function for the adapter integration
export const createStreamingResponse = async (
  sessionId: string,
  recommendation: string
): Promise<Response> => {
  const baseUrl =
    localStorage.getItem("baseApiUrl") ||
    import.meta.env.REACT_APP_API_BASE_URL ||
    "";

  // Use fetch for SSE
  const authHeader = axios.defaults.headers.common["Authorization"] as string;
  const response = await fetch(`${baseUrl}/api/v1/adonis/recommendation`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      "Cache-Control": "no-cache",
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
    body: JSON.stringify({ sessionId, recommendation }),
  });

  if (!response.ok) {
    throw new Error(`API returned status ${response.status}`);
  }

  if (!response.body) {
    throw new Error("ReadableStream not supported");
  }

  return response;
};

export const getAdonisRecommendationsBySession = async (
  sessionId: string
): Promise<AdonisRecommendation[]> => {
  try {
    const id = sessionId;
    const recommendations = await apiGet(
      "/adonis/recommendations/session/{id}",
      {
        id,
      }
    );
    if (!recommendations) {
      console.error("No response from API");
      return [];
    }

    return recommendations.map((msg: AdonisRecommendationModel) => ({
      role: msg.userQuery ? "user" : "assistant",
      userQuery: msg.userQuery,
      adonisResponse: msg.adonisResponse,
      createdAt: msg.createdAt,
      updatedAt: msg.updatedAt,
    }));
  } catch (error) {
    console.error("Error getting recommendations:", error);
    return [];
  }
};

export const getAdonisStarterQuestions = async (): Promise<
  { id: string; text: string }[]
> => {
  try {
    const response = await apiGet("/adonis/starter-questions");

    if (!response || !Array.isArray(response)) {
      console.error("Invalid response from starter questions API");
      return [];
    }

    return response
      .filter((question: string) => question && question.trim())
      .map((question: string, index: number) => ({
        id: (index + 1).toString(),
        text: question.trim(),
      }));
  } catch (error) {
    console.error("Error getting starter questions:", error);
    return [];
  }
};

const trackAdonisLinkClicked = (): void => {
  Analytics.track(AnalyticsEvents.ADONIS_LINK_CLICKED, {});
};

export const createPdpLinkHandler = (navigate: (path: string) => void) => {
  return (href: string) => {
    const linkId = `link-${crypto.randomUUID()}`;

    if (typeof window !== "undefined") {
      // @ts-expect-error - pdpNavigate is not defined in the window object
      window.pdpNavigate = window.pdpNavigate ?? {};
      // @ts-expect-error - pdpNavigate is not defined in the window object
      window.pdpNavigate[linkId] = () => {
        trackAdonisLinkClicked();
        return navigate(href);
      };
    }

    return linkId;
  };
};

const toTitleCase = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const replacePersonaPlaceholders = (
  text: string,
  identity: {
    name: string;
    pronoun: string;
    objectPronoun: string;
  }
): string => {
  if (!text || !identity) return text;

  return text
    .replace(/{NAME}/g, identity.name.toUpperCase())
    .replace(/{Name}/g, toTitleCase(identity.name))
    .replace(/{name}/g, identity.name.toLowerCase())
    .replace(/{PRONOUN}/g, identity.pronoun.toUpperCase())
    .replace(/{Pronoun}/g, toTitleCase(identity.pronoun))
    .replace(/{pronoun}/g, identity.pronoun.toLowerCase())
    .replace(/{OBJECTPRONOUN}/g, identity.objectPronoun.toUpperCase())
    .replace(/{ObjectPronoun}/g, toTitleCase(identity.objectPronoun))
    .replace(/{objectPronoun}/g, identity.objectPronoun.toLowerCase());
};
